import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FileSpreadsheet, FileText, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { jsPDF } from 'jspdf';
import { useIsMobile } from '@/hooks/use-mobile';
import { format } from 'date-fns';
import { useLanguage } from '@/contexts/LanguageContext';

const ExportInvoiceReportModal = ({ open, onOpenChange, reportData, dateRange, currency }) => {
  const { t, locale } = useLanguage();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [formatType, setFormatType] = useState("csv");
  const [loading, setLoading] = useState(false);
  const currencySymbol = currency || 'USD';

  const handleExport = async () => {
  setLoading(true);
  try {
    if (formatType === "csv") {
      const csvData = convertToCSV(reportData);
      downloadFile(csvData, "invoice_report.csv", "text/csv");
    } else if (formatType === "pdf") {
      await generatePDF(reportData, dateRange);
    }
    toast({
      title: t('reports.exportComplete'),
      description: t('reports.exportCompleteDesc'),
    });
  } catch (error) {
    toast({
      title: t('reports.exportFailed'),
      description: t('reports.exportFailedDesc'),
      variant: "destructive",
    });
  } finally {
    setLoading(false);
    onOpenChange(false);
  }
};


const getMonthName = (num) => {
  const mois = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
  return mois[num - 1] || "";
};



  const convertToCSV = (data) => {
    const headers = ["Month", "Revenue", "Paid", "Pending", "Invoice"];

    let totalRevenue = 0;
    let totalPaid = 0;
    let totalPending = 0;
    let totalCount = 0;

    const rows = data.monthly.map(month => {
      totalRevenue += month.revenue;
      totalPaid += month.paid;
      totalPending += month.pending;
      totalCount += month.count;

      return [
        month.month,
        month.revenue,
        month.paid,
        month.pending,
        month.count,
      ];
    });

    // Ajout de la ligne TOTAL
    const totalRow = ["TOTAL", totalRevenue, totalPaid, totalPending, totalCount];

    return "\uFEFF" + [headers, ...rows, totalRow].map(row => row.join(";")).join("\n");
  };

 


  const downloadFile = (data, filename, type) => {
    const file = new Blob([data], { type: type });
    const a = document.createElement("a");
    const url = URL.createObjectURL(file);
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 0);
  };

  const generatePDF = async (data, dateRange) => {
  const doc = new jsPDF();

  // Define colors
  const primaryColor = [70, 130, 180]; // Steel blue for headers
  const borderColor = [200, 200, 200]; // Gray for borders

  // Add title
  doc.setFontSize(18);
  doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.text(locale === "fr" ? "Rapport de factures" : "Invoice Report", 105, 10, { align: 'center' });

  // Add date range
  const dateRangeText = dateRange.from && dateRange.to
    ? `${format(dateRange.from, 'MMM dd, yyyy')} - ${format(dateRange.to, 'MMM dd, yyyy')}`
    : 'Date range not available';
  doc.setFontSize(12);
  doc.setTextColor(100);
  doc.text(dateRangeText, 105, 20, { align: 'center' });

  // Draw summary boxes with margins
    const summaryData = [
    { title: locale === 'fr' ? "Revenu total" : "Total Revenue", value: `${data.summary.totalRevenue.toFixed(2)} ${currencySymbol}` },
    { title: locale === 'fr' ? "Total payé" : "Total Paid", value: `${data.summary.totalPaid.toFixed(2)} ${currencySymbol}` },
    { title: locale === 'fr' ? "Total en attente" : "Total Pending", value: `${data.summary.totalPending.toFixed(2)} ${currencySymbol}` },
    { title: locale === 'fr' ? "Total des factures" : "Total Invoices", value: `${data.summary.totalInvoices}` },
  ];

  const boxWidth = 45;
  const boxHeight = 28;
  const margin = 5; // Margin around boxes
  const startY = 30;
  const totalWidth = boxWidth * summaryData.length + margin * (summaryData.length - 1);
  const startX = (doc.internal.pageSize.getWidth() - totalWidth) / 2; // Center the boxes

  summaryData.forEach((item, index) => {
    const x = startX + index * (boxWidth + margin);

    // Draw rounded box
    doc.setFillColor(220, 230, 241); // Light blue for background
    doc.roundedRect(x, startY, boxWidth, boxHeight, 3, 3, 'F');

    // Add title
    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.text(item.title, x + boxWidth / 2, startY + 10, { align: 'center' });

    // Add value
    doc.setFontSize(14);
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text(item.value, x + boxWidth / 2, startY + 20, { align: 'center' });
  });

  // Add table title
  doc.setFontSize(14);
  doc.setTextColor(0);
  //doc.text("Rapport mensuel", 105, 80, { align: 'center' });

  // Define table columns and rows
  const tableColumn = locale === "fr"
    ? ["Mois", "Revenu", "Taxes", "Frais", "Payé", "En attente", "Factures"]
    : ["Month", "Revenue", "Taxes", "Expenses", "Paid", "Pending", "Invoices"];
  const tableRows = data.monthly.map(month => {
  const taxesText = month.taxes 
      ? Object.entries(month.taxes).map(([taxName, amount]) => `${taxName}: ${amount} ${currencySymbol}`).join("\n")
      : "-";
      const expensesText = month.totalExpenses.toFixed(2) + ' ' + currencySymbol;

    return [
      t(`reports.months.${month.month}`),
      `${month.revenue.toFixed(2)} ${currencySymbol}`,
      taxesText,
      expensesText,
      `${month.paid.toFixed(2)} ${currencySymbol}`,
      `${month.pending.toFixed(2)} ${currencySymbol}`,
      month.count,
    ];
  });

  const totalTaxesText = data.summary.totalTaxes
    ? Object.entries(data.summary.totalTaxes)
        .map(([taxName, amount]) => `${taxName}: ${amount} ${currencySymbol}`)
        .join("\n")
    : "-";
    const totalExpensesText = data.summary.totalExpenses
  ? `${data.summary.totalExpenses.toFixed(2)} ${currencySymbol}`
  : "-";

  tableRows.push([
    "TOTAL",
    `${data.summary.totalRevenue.toFixed(2)} ${currencySymbol}`,
    totalTaxesText,
    totalExpensesText,
    `${data.summary.totalPaid.toFixed(2)} ${currencySymbol}`,
    `${data.summary.totalPending.toFixed(2)} ${currencySymbol}`,
    data.summary.totalInvoices,
  ]);

  // Calculate the total width of the table
  //const colWidth = 40;
  const pageWidth = doc.internal.pageSize.getWidth();
  const colWidth = pageWidth / tableColumn.length - 5;
  const totalTableWidth = colWidth * tableColumn.length;
  const startTableX = (doc.internal.pageSize.getWidth() - totalTableWidth) / 2; // Center the table

  // Draw table manually with margins
  const startYY = 70;
  const rowHeight = 10;
  const rowMargin = 2; // Margin between rows

  // Draw headers without background color
  tableColumn.forEach((header, index) => {
    doc.setFontSize(11);
    doc.setTextColor(0); // Noir pour le texte
    doc.text(header, startTableX + index * colWidth + colWidth / 2, startYY + rowHeight / 2, { align: 'center' });
    // Ajout des petits textes sous Revenue, Paid et Pending
    if (index === 1) { // Revenue
      doc.setFontSize(8);
      doc.text(locale === "fr" ? "(H.T)" : "(Before tax)", startTableX + index * colWidth + colWidth / 2, startYY + rowHeight / 2 + 5, { align: 'center' });
    } else if (index === 4 || index === 5) { // Paid et Pending
      doc.setFontSize(8);
      doc.text(locale === "fr" ? "(T.T.C)" : "(Including tax)", startTableX + index * colWidth + colWidth / 2, startYY + rowHeight / 2 + 5, { align: 'center' });
    }
  });

  // Draw rows with bottom border only
  tableRows.forEach((row, rowIndex) => {
    let y = startYY + (rowIndex + 1) * (rowHeight + rowMargin);

  // ⚙️ Correction : si c’est la dernière ligne (TOTAL), on retire la marge du dessus
  if (rowIndex === tableRows.length - 1) {
    y -= rowMargin;
    doc.setFillColor(230, 230, 230); // gris clair
    doc.rect(startTableX, y, totalTableWidth, rowHeight, 'F'); // 'F' pour fill
  }

    // Draw bottom border for the row
    doc.setDrawColor(borderColor[0], borderColor[1], borderColor[2]); // Gray for the border
    doc.setLineWidth(0.1);
    doc.line(startTableX, y + rowHeight, startTableX + totalTableWidth, y + rowHeight);

    // Draw cells
    row.forEach((cell, colIndex) => {
      doc.setFontSize(10);
      doc.setTextColor(0); // Noir pour le texte
      //doc.text(cell.toString(), startTableX + colIndex * colWidth + colWidth / 2, y + rowHeight / 2, { align: 'center' });
       if ([2, 3].includes(colIndex) && typeof cell === 'string') { 
        const lines = cell.split("\n");
        doc.setFontSize(8); // ← texte plus petit pour la colonne Taxes
        lines.forEach((line, i) => {
          doc.text(
            line,
            startTableX + colIndex * colWidth + colWidth / 2,
            y + rowHeight / 2 + i * 4, // 4 ou 5 selon l'espacement que tu veux
            { align: 'center' }
          );
        });
      } else {
        doc.setFontSize(10); // taille normale pour les autres colonnes
        doc.text(
          cell.toString(),
          startTableX + colIndex * colWidth + colWidth / 2,
          y + rowHeight / 2,
          { align: 'center' }
        );
      }
    });
  });

   
  // === PAYMENT METHODS SECTION ===
  if (data.paymentMethods && Object.keys(data.paymentMethods).length > 0) {
    let y = startYY + (tableRows.length + 1) * (rowHeight + rowMargin) + 20;

    const pageHeight = doc.internal.pageSize.getHeight();
    if (y > pageHeight - 40) {
      doc.addPage();
      y = 40;
    }

    doc.setFontSize(14);
    doc.setTextColor(70, 130, 180);
    doc.text(locale === "fr" ? "Méthodes de paiement" : "Payment Methods", 105, y, { align: "center" });

    y += 10;

    const paymentEntries = Object.entries(data.paymentMethods)
      .filter(([method]) => method.toLowerCase() !== "pending");

    if (paymentEntries.length > 0) {
      const tableColumn = locale === "fr"
        ? ["Méthode", "Montant"]
        : ["Method", "Amount"];

      const tableRows = paymentEntries.map(([method, amount]) => [
        t(`reports.${method}`),
        `${(amount as number).toFixed(2)} ${currencySymbol}`,
      ]);

      const pageWidth = doc.internal.pageSize.getWidth();
      const colWidth = (pageWidth - 60) / tableColumn.length;
      const totalTableWidth = colWidth * tableColumn.length;
      const startX = (pageWidth - totalTableWidth) / 2;

      const borderColor = [200, 200, 200];
      const rowHeight = 10;
      const rowMargin = 2;
      let currentY = y + 5;

      doc.setFontSize(12);
      doc.setTextColor(0);
      tableColumn.forEach((header, index) => {
        doc.text(header, startX + index * colWidth + colWidth / 2, currentY + rowHeight / 2, { align: "center" });
      });

      tableRows.forEach((row, rowIndex) => {
        const yRow = currentY + (rowIndex + 1) * rowHeight + rowIndex * rowMargin;

        doc.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
        doc.setLineWidth(0.1);
        doc.line(startX, yRow + rowHeight, startX + totalTableWidth, yRow + rowHeight);

        row.forEach((cell, colIndex) => {
          doc.setFontSize(10);
          doc.setTextColor(0);
          doc.text(cell.toString(), startX + colIndex * colWidth + colWidth / 2, yRow + rowHeight / 2, { align: "center" });
        });
      });
    }
  }

  doc.save('invoice_report.pdf');
};










  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[600px]"}>
        <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              {t('reports.exportReport')}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 mt-4 ${isMobile ? 'p-5' : ''}`}>

          <Card className="p-3 border rounded-md hover:bg-muted/50 cursor-pointer transition-colors flex flex-col items-center justify-center text-center" onClick={() => setFormatType('csv')}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${formatType === 'csv' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
              <FileSpreadsheet className="h-5 w-5" />
            </div>
            <div className="font-medium">CSV</div>
            <div className="text-xs text-muted-foreground">Excel compatible</div>
          </Card>

          <Card className="p-3 border rounded-md hover:bg-muted/50 cursor-pointer transition-colors flex flex-col items-center justify-center text-center" onClick={() => setFormatType('pdf')}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${formatType === 'pdf' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
              <FileText className="h-5 w-5" />
            </div>
            <div className="font-medium">PDF</div>
            <div className="text-xs text-muted-foreground">Print ready</div>
          </Card>
        </div>

        <DialogFooter className={isMobile ? "px-6 py-4 border-t sticky bottom-0 bg-gray-100 z-20" : "pt-5"}>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="default"
            onClick={handleExport}
            disabled={loading}
          >
            {loading ? t('common.processing') : t('reports.exportNow')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportInvoiceReportModal;
